/** Festival theme ids selectable from the Remote Config console (plan §6). */
export const THEME_IDS = [
  'default',
  'diwali',
  'holi',
  'navratri',
  'durga_puja',
  'ganesh_chaturthi',
  'onam',
  'pongal_sankranti',
  'raksha_bandhan',
  'independence_day',
  'republic_day',
  'eid',
  'christmas_newyear',
] as const;
export type ThemeId = (typeof THEME_IDS)[number];

export const isThemeId = (value: unknown): value is ThemeId =>
  typeof value === 'string' && (THEME_IDS as readonly string[]).includes(value);

export interface FeatureFlags {
  practice_mode: boolean;
  band_mode: boolean;
  quick_play: boolean;
  sound_default: boolean;
  push_soft_ask_after_matches: number;
}

export interface ThemeOverrides {
  /** CSS-variable token overrides, e.g. { "accent": "#f5b301" }. */
  palette?: Record<string, string>;
  /** Banner greeting per language code. */
  banner?: Record<string, string>;
  /** 0–1 multiplier for ambient particles. */
  ambientIntensity?: number;
  /** Optional remote asset URLs keyed by slot (felt, cardBack, celebration). */
  assets?: Record<string, string>;
}

export interface MaintenanceConfig {
  enabled: boolean;
  message?: string;
}

export interface Announcement {
  id?: string;
  title?: string;
  body?: string;
  link?: string;
}

export const RC_KEYS = {
  themeId: 'theme_id',
  themeOverrides: 'theme_overrides',
  themeBannerEnabled: 'theme_banner_enabled',
  featureFlags: 'feature_flags',
  turnTimeoutDefaultSec: 'turn_timeout_default_sec',
  minSupportedVersion: 'min_supported_version',
  maintenance: 'maintenance',
  announcement: 'announcement',
} as const;

export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  practice_mode: true,
  band_mode: true,
  quick_play: false,
  sound_default: true,
  push_soft_ask_after_matches: 1,
};

/** In-app defaults (also the source for firebase/remoteconfig.template.json). */
export const RC_DEFAULTS: Record<string, string | number | boolean> = {
  [RC_KEYS.themeId]: 'default',
  [RC_KEYS.themeOverrides]: '{}',
  [RC_KEYS.themeBannerEnabled]: false,
  [RC_KEYS.featureFlags]: JSON.stringify(DEFAULT_FEATURE_FLAGS),
  [RC_KEYS.turnTimeoutDefaultSec]: 30,
  [RC_KEYS.minSupportedVersion]: '0.0.0',
  [RC_KEYS.maintenance]: JSON.stringify({ enabled: false }),
  [RC_KEYS.announcement]: '{}',
};

export interface RemoteSettings {
  themeId: ThemeId;
  themeOverrides: ThemeOverrides;
  themeBannerEnabled: boolean;
  featureFlags: FeatureFlags;
  turnTimeoutDefaultSec: number;
  minSupportedVersion: string;
  maintenance: MaintenanceConfig;
  announcement: Announcement;
}

function safeJson<T>(raw: string, fallback: T): T {
  try {
    const value = JSON.parse(raw) as unknown;
    return typeof value === 'object' && value !== null ? (value as T) : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Converts raw Remote Config strings into typed settings. Unknown theme ids (newer than this build)
 * fall back to 'default' so the console can never break an old client.
 */
export function parseRemoteSettings(get: (key: string) => string): RemoteSettings {
  const themeRaw = get(RC_KEYS.themeId);
  const timeout = Number(get(RC_KEYS.turnTimeoutDefaultSec));
  return {
    themeId: isThemeId(themeRaw) ? themeRaw : 'default',
    themeOverrides: safeJson<ThemeOverrides>(get(RC_KEYS.themeOverrides), {}),
    themeBannerEnabled: get(RC_KEYS.themeBannerEnabled) === 'true',
    featureFlags: {
      ...DEFAULT_FEATURE_FLAGS,
      ...safeJson<Partial<FeatureFlags>>(get(RC_KEYS.featureFlags), {}),
    },
    turnTimeoutDefaultSec:
      Number.isFinite(timeout) && timeout >= 10 && timeout <= 120 ? timeout : 30,
    minSupportedVersion: get(RC_KEYS.minSupportedVersion) || '0.0.0',
    maintenance: safeJson<MaintenanceConfig>(get(RC_KEYS.maintenance), { enabled: false }),
    announcement: safeJson<Announcement>(get(RC_KEYS.announcement), {}),
  };
}

export const DEFAULT_REMOTE_SETTINGS: RemoteSettings = parseRemoteSettings((key) =>
  String(RC_DEFAULTS[key] ?? ''),
);

/** Compares dotted versions; negative when a < b. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => Number.parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}
